import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect, useCallback } from 'react'
import type { ApplicationSection, ApplicationField, ApplicationFieldType } from '@/types'
import { adminFormBuilderService } from '../services/admin.formBuilder.service'
import { adminFieldLibraryService } from '../services/admin.fieldLibrary.service'
import { adminSectionLibraryService } from '../services/admin.sectionLibrary.service'

function generateId(): string {
  return crypto.randomUUID()
}

function defaultFieldForType(type: ApplicationFieldType): Omit<ApplicationField, 'id' | 'order'> {
  const base: Omit<ApplicationField, 'id' | 'order'> = { type, label: fieldTypeLabel(type), required: false }
  switch (type) {
    case 'linear_scale': return { ...base, min: 1, max: 5, minLabel: '', maxLabel: '' }
    case 'dropdown':
    case 'multiple_choice':
    case 'checkbox_group': return { ...base, options: ['Option 1', 'Option 2'] }
    case 'file_upload': return { ...base, maxFileSizeMB: 10, acceptedFileTypes: [] }
    case 'text_list': return base
    case 'payment': return { ...base, paymentAmountCents: 2500, paymentCurrency: 'usd' }
    case 'notice': return { ...base, label: '', description: '' }
    default: return base
  }
}

function fieldTypeLabel(type: ApplicationFieldType): string {
  const labels: Record<ApplicationFieldType, string> = {
    short_text: 'Short Text',
    long_text: 'Long Text',
    email: 'Email',
    phone: 'Phone',
    url: 'URL',
    number: 'Number',
    date: 'Date',
    dropdown: 'Dropdown',
    multiple_choice: 'Multiple Choice',
    checkbox_group: 'Checkbox Group',
    linear_scale: 'Linear Scale',
    file_upload: 'File Upload',
    mailing_list: 'Mailing List Opt-In',
    text_list: 'Text List',
    payment: 'Payment',
    notice: 'Notice',
  }
  return labels[type]
}

interface UseAdminFormBuilderResult {
  formId: string | null
  sections: ApplicationSection[]
  loading: boolean
  saving: boolean
  error: string | null
  isDirty: boolean
  // Section ops
  addSection: () => void
  updateSection: (sectionId: string, patch: Partial<Pick<ApplicationSection, 'title' | 'description'>>) => void
  deleteSection: (sectionId: string) => void
  reorderSections: (orderedIds: string[]) => void
  // Field ops
  addSectionFromLibrary: (libraryId: string) => Promise<void>
  addField: (sectionId: string, type: ApplicationFieldType) => void
  addFieldFromLibrary: (sectionId: string, libraryId: string) => Promise<void>
  updateField: (sectionId: string, fieldId: string, patch: Partial<ApplicationField>) => void
  deleteField: (sectionId: string, fieldId: string) => void
  reorderFields: (sectionId: string, orderedIds: string[]) => void
  moveField: (fromSectionId: string, fieldId: string, toSectionId: string, toIndex: number) => void
  // Persist
  save: () => Promise<void>
}

export function useAdminFormBuilder(applicationId: string): UseAdminFormBuilderResult {
  const [formId, setFormId] = useState<string | null>(null)
  const [sections, setSections] = useState<ApplicationSection[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    setLoading(true)
    adminFormBuilderService
      .getByApplicationId(applicationId)
      .then(async (form) => {
        if (form) {
          setFormId(form.id)
          setSections(form.sections ?? [])
        } else {
          const id = await adminFormBuilderService.create(applicationId)
          setFormId(id)
          setSections([])
        }
      })
      .catch((err) => setError(formatUserFacingError(err, 'Failed to load form')))
      .finally(() => setLoading(false))
  }, [applicationId])

  const markDirty = useCallback(() => setIsDirty(true), [])

  const addSection = useCallback(() => {
    setSections((prev) => {
      const newSection: ApplicationSection = {
        id: generateId(),
        title: 'New Section',
        order: prev.length,
        fields: [],
      }
      return [...prev, newSection]
    })
    markDirty()
  }, [markDirty])

  const updateSection = useCallback(
    (sectionId: string, patch: Partial<Pick<ApplicationSection, 'title' | 'description'>>) => {
      setSections((prev) =>
        prev.map((s) => (s.id === sectionId ? { ...s, ...patch } : s))
      )
      markDirty()
    },
    [markDirty]
  )

  const deleteSection = useCallback((sectionId: string) => {
    setSections((prev) => prev.filter((s) => s.id !== sectionId))
    markDirty()
  }, [markDirty])

  const reorderSections = useCallback((orderedIds: string[]) => {
    setSections((prev) => {
      const map = Object.fromEntries(prev.map((s) => [s.id, s]))
      return orderedIds.map((id, index) => ({ ...map[id]!, order: index }))
    })
    markDirty()
  }, [markDirty])

  const addSectionFromLibrary = useCallback(
    async (libraryId: string) => {
      const template = await adminSectionLibraryService.getById(libraryId)
      if (!template) return
      setSections((prev) => {
        const newSection: ApplicationSection = {
          id: generateId(),
          title: template.title,
          description: template.description,
          order: prev.length,
          fields: (template.fields ?? []).map((f, index) => ({
            ...f,
            id: generateId(),
            order: index,
          })),
        }
        return [...prev, newSection]
      })
      markDirty()
    },
    [markDirty]
  )

  const addField = useCallback(
    (sectionId: string, type: ApplicationFieldType) => {
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== sectionId) return s
          const newField: ApplicationField = {
            id: generateId(),
            order: s.fields.length,
            ...defaultFieldForType(type),
          }
          return { ...s, fields: [...s.fields, newField] }
        })
      )
      markDirty()
    },
    [markDirty]
  )

  const addFieldFromLibrary = useCallback(
    async (sectionId: string, libraryId: string) => {
      const libraryField = await adminFieldLibraryService.getById(libraryId)
      if (!libraryField) return
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== sectionId) return s
          const newField: ApplicationField = {
            id: generateId(),
            fieldLibraryId: libraryId,
            type: libraryField.type,
            label: libraryField.label,
            description: libraryField.description,
            placeholder: libraryField.placeholder,
            required: false,
            order: s.fields.length,
            options: libraryField.options,
            min: libraryField.min,
            max: libraryField.max,
            integerOnly: libraryField.integerOnly,
            minLabel: libraryField.minLabel,
            maxLabel: libraryField.maxLabel,
            maxFileSizeMB: libraryField.maxFileSizeMB,
            acceptedFileTypes: libraryField.acceptedFileTypes,
          }
          return { ...s, fields: [...s.fields, newField] }
        })
      )
      markDirty()
    },
    [markDirty]
  )

  const updateField = useCallback(
    (sectionId: string, fieldId: string, patch: Partial<ApplicationField>) => {
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== sectionId) return s
          return {
            ...s,
            fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)),
          }
        })
      )
      markDirty()
    },
    [markDirty]
  )

  const deleteField = useCallback(
    (sectionId: string, fieldId: string) => {
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== sectionId) return s
          return { ...s, fields: s.fields.filter((f) => f.id !== fieldId) }
        })
      )
      markDirty()
    },
    [markDirty]
  )

  const reorderFields = useCallback(
    (sectionId: string, orderedIds: string[]) => {
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== sectionId) return s
          const map = Object.fromEntries(s.fields.map((f) => [f.id, f]))
          return {
            ...s,
            fields: orderedIds.map((id, index) => ({ ...map[id]!, order: index })),
          }
        })
      )
      markDirty()
    },
    [markDirty]
  )

  const moveField = useCallback(
    (fromSectionId: string, fieldId: string, toSectionId: string, toIndex: number) => {
      setSections((prev) => {
        const fromSection = prev.find((s) => s.id === fromSectionId)
        if (!fromSection) return prev
        const field = fromSection.fields.find((f) => f.id === fieldId)
        if (!field) return prev

        if (fromSectionId === toSectionId) {
          const remaining = fromSection.fields.filter((f) => f.id !== fieldId)
          const clamped = Math.max(0, Math.min(toIndex, remaining.length))
          remaining.splice(clamped, 0, field)
          return prev.map((s) =>
            s.id === fromSectionId
              ? { ...s, fields: remaining.map((f, i) => ({ ...f, order: i })) }
              : s
          )
        }

        return prev.map((s) => {
          if (s.id === fromSectionId) {
            const remaining = s.fields.filter((f) => f.id !== fieldId)
            return { ...s, fields: remaining.map((f, i) => ({ ...f, order: i })) }
          }
          if (s.id === toSectionId) {
            const next = [...s.fields]
            const clamped = Math.max(0, Math.min(toIndex, next.length))
            next.splice(clamped, 0, field)
            return { ...s, fields: next.map((f, i) => ({ ...f, order: i })) }
          }
          return s
        })
      })
      markDirty()
    },
    [markDirty]
  )

  const save = useCallback(async () => {
    if (!formId) return
    setSaving(true)
    try {
      await adminFormBuilderService.save(formId, sections)
      setIsDirty(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save form'))
    } finally {
      setSaving(false)
    }
  }, [formId, sections])

  return {
    formId,
    sections,
    loading,
    saving,
    error,
    isDirty,
    addSection,
    updateSection,
    deleteSection,
    reorderSections,
    addSectionFromLibrary,
    addField,
    addFieldFromLibrary,
    updateField,
    deleteField,
    reorderFields,
    moveField,
    save,
  }
}

export { fieldTypeLabel }
